import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { CustomTypeOptions, TFunction } from 'i18next';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
} from '@/hooks/useNotifications';
import type { AppNotification } from '@/services/notifications.service';
import {
  Bell,
  Trophy,
  Info,
  CheckCheck,
  Loader2,
  BookOpen,
  CreditCard,
  ArrowUp,
  BellOff,
  CheckCircle2,
  Flame,
  Award,
  Star,
  MessageCircle,
  Heart,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useLocaleFormat } from '@/i18n/locale';
import { notificationText } from '@/lib/notification-text';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
} from '@xtreed/academy-shared/ui';

type PointsReasonKey =
  keyof CustomTypeOptions['resources']['common']['notifications']['reasons'];

// Map action metadata to human-readable reason
function getPointsReason(
  t: TFunction,
  metadata?: Record<string, unknown>,
): string | null {
  if (!metadata?.action) return null;
  const reasonKeys: Record<string, PointsReasonKey> = {
    lesson_completed: 'lessonCompleted',
    module_completed: 'moduleCompleted',
    course_completed: 'courseCompleted',
    comment_posted: 'commentPosted',
    daily_login: 'dailyLogin',
    streak_bonus: 'streakBonus',
    quiz_completed: 'quizCompleted',
    certificate_earned: 'certificateEarned',
    exercise_completed: 'exerciseCompleted',
    project_submitted: 'projectSubmitted',
    help_colleague: 'helpColleague',
    first_lesson: 'firstLesson',
    community_post_created: 'communityPostCreated',
    community_comment_created: 'communityCommentCreated',
    community_post_liked: 'communityPostLiked',
    community_comment_liked: 'communityCommentLiked',
    community_mentioned: 'communityMentioned',
    course_started: 'courseStarted',
    course_25_percent: 'courseProgress',
    course_50_percent: 'courseProgress',
    course_75_percent: 'courseProgress',
    lesson_favorited: 'lessonFavorited',
    post_liked: 'postLiked',
    post_replied: 'postReplied',
    post_shared: 'postShared',
    daily_checklist_bonus: 'dailyChecklistBonus',
  };
  const key = reasonKeys[metadata.action as string];
  return key ? t(`notifications.reasons.${key}`) : null;
}

function getNotificationMeta(notification: AppNotification) {
  const { category, type, metadata } = notification;
  const action = (metadata as Record<string, unknown>)?.action as string;

  switch (category) {
    case 'comment':
      if (
        notification.code === 'COMMENT_REACTION' ||
        (type === 'info' && notification.title?.includes('curtida'))
      ) {
        return {
          icon: Heart,
          color: 'text-xtreed-lilac',
          bg: 'bg-xtreed-lilac/15',
        };
      }
      return {
        icon: MessageCircle,
        color: 'text-info-ink',
        bg: 'bg-info/15',
      };
    case 'gamification':
      if (type === 'success') {
        return {
          icon: ArrowUp,
          color: 'text-warning-ink',
          bg: 'bg-warning/15',
        };
      }
      if (action === 'streak_bonus') {
        return {
          icon: Flame,
          color: 'text-warning-ink',
          bg: 'bg-warning/15',
        };
      }
      if (action === 'course_completed') {
        return {
          icon: Award,
          color: 'text-success-ink',
          bg: 'bg-success/15',
        };
      }
      if (action === 'certificate_earned') {
        return { icon: Star, color: 'text-warning-ink', bg: 'bg-warning/15' };
      }
      return {
        icon: Trophy,
        color: 'text-warning-ink',
        bg: 'bg-warning/15',
      };
    case 'community':
      return {
        icon: Users,
        color: 'text-primary-ink',
        bg: 'bg-primary/15',
      };
    case 'course':
      return {
        icon: BookOpen,
        color: 'text-success-ink',
        bg: 'bg-success/15',
      };
    case 'lesson':
      return {
        icon: CheckCircle2,
        color: 'text-success-ink',
        bg: 'bg-success/15',
      };
    case 'payment':
      return {
        icon: CreditCard,
        color: 'text-accent',
        bg: 'bg-accent/15',
      };
    default:
      return {
        icon: Info,
        color: 'text-info-ink',
        bg: 'bg-xtreed-cyan/15',
      };
  }
}

function timeAgo(dateStr: string, t: TFunction, intlLocale: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return t('notifications.timeAgo.now');
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleString(intlLocale, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const GROUPABLE_CODES = ['POINTS_EARNED', 'COMMUNITY_POINTS_EARNED'];

interface NotificationGroup {
  notification: AppNotification;
  unreadIds: string[];
  count: number;
  points: number;
}

function dayKey(dateStr: string) {
  return new Date(dateStr).toDateString();
}

function groupNotifications(list: AppNotification[]): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  for (const notification of list) {
    const points = Number(notification.params?.points ?? 0);
    const last = groups[groups.length - 1];
    const groupable =
      !!notification.code && GROUPABLE_CODES.includes(notification.code);
    if (
      groupable &&
      last &&
      last.notification.code &&
      GROUPABLE_CODES.includes(last.notification.code) &&
      dayKey(last.notification.createdAt) === dayKey(notification.createdAt)
    ) {
      last.count += 1;
      last.points += points;
      if (!notification.read) last.unreadIds.push(notification.id);
      continue;
    }
    groups.push({
      notification,
      unreadIds: notification.read ? [] : [notification.id],
      count: 1,
      points,
    });
  }
  return groups;
}

function NotificationItem({
  notification,
  grouped,
  onRead,
}: {
  notification: AppNotification;
  grouped?: { count: number; points: number; unread: boolean };
  onRead: () => void;
}) {
  const { t } = useTranslation('common');
  const { intlLocale } = useLocaleFormat();
  const meta = getNotificationMeta(notification);
  const Icon = meta.icon;
  const text = notificationText(notification);
  const title = grouped
    ? t('notifications.grouped.title', { points: grouped.points })
    : text.title;
  const message = grouped
    ? t('notifications.grouped.message', {
        count: grouped.count,
        points: grouped.points,
      })
    : text.message;
  const read = grouped ? !grouped.unread : notification.read;
  const reason =
    !grouped &&
    (notification.category === 'gamification' ||
      notification.category === 'community')
      ? getPointsReason(t, notification.metadata as Record<string, unknown>)
      : null;

  return (
    <button
      onClick={onRead}
      className={cn(
        'group relative flex w-full items-start gap-3 px-4 py-3 text-left transition-all duration-200',
        'hover:bg-muted/50 active:bg-muted/70',
        !read && 'bg-primary/3',
      )}
    >
      {/* Unread indicator line */}
      {!read && (
        <div className="absolute top-2 bottom-2 left-0 w-[3px] rounded-r-full bg-primary" />
      )}

      {/* Icon */}
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-105',
          read ? 'bg-muted' : meta.bg,
        )}
      >
        <Icon
          className={cn('h-4 w-4', read ? 'text-muted-foreground' : meta.color)}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-[13px] leading-snug',
              notification.read
                ? 'text-muted-foreground'
                : 'font-medium text-foreground',
            )}
          >
            {title}
          </p>
          <span className="mt-0.5 shrink-0 text-[10px] text-muted-foreground/60">
            {timeAgo(notification.createdAt, t, intlLocale)}
          </span>
        </div>
        <p
          className={cn(
            'mt-0.5 line-clamp-2 text-xs leading-relaxed',
            notification.read
              ? 'text-muted-foreground/60'
              : 'text-muted-foreground',
          )}
        >
          {message}
        </p>

        {/* Reason tag for gamification */}
        {reason && (
          <span
            className={cn(
              'mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
              notification.read
                ? 'bg-muted text-muted-foreground/60'
                : notification.category === 'community'
                  ? 'bg-primary/10 text-primary-ink'
                  : 'bg-warning/10 text-warning-ink',
            )}
          >
            {reason}
          </span>
        )}

        {/* Clickable hint */}
        {notification.actionUrl && !read && (
          <p className="mt-1 text-[10px] font-medium text-primary-ink opacity-0 transition-opacity group-hover:opacity-100">
            {t('notifications.clickToView')} &rarr;
          </p>
        )}
      </div>
    </button>
  );
}

type FilterTab = 'all' | 'unread';

export function NotificationDropdown() {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterTab>('all');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notificationsData, isLoading } = useNotifications(1, 50);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  // Refetch when dropdown opens
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }, [open, queryClient]);

  // Auto-refresh notifications every 10s when dropdown is open
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }, 10000);
    return () => clearInterval(interval);
  }, [open, queryClient]);

  // Animate bell on new notifications
  const [bellAnimating, setBellAnimating] = useState(false);
  const prevCountRef = useRef(unreadCount);
  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setBellAnimating(true);
      const timer = setTimeout(() => setBellAnimating(false), 1000);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      prevCountRef.current = unreadCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount, queryClient]);

  const notifications = notificationsData?.data || [];

  const filtered =
    filter === 'unread'
      ? notifications.filter((n: AppNotification) => !n.read)
      : notifications;

  const groups = groupNotifications(filtered);

  const handleRead = useCallback(
    (notification: AppNotification, unreadIds: string[]) => {
      unreadIds.forEach((id) => markAsRead.mutate(id));

      // Navigate based on category and actionUrl
      if (notification.actionUrl) {
        // Fix legacy URL patterns
        let url = notification.actionUrl;
        if (url.startsWith('/courses/lesson/')) {
          url = url.replace('/courses/lesson/', '/lesson/');
        }
        navigate(url);
        setOpen(false);
      } else if (notification.category === 'community') {
        navigate('/community');
        setOpen(false);
      } else if (notification.category === 'gamification') {
        navigate('/ranking');
        setOpen(false);
      } else if (notification.category === 'comment') {
        const meta = notification.metadata as Record<string, unknown>;
        if (meta?.lessonId) {
          navigate(`/lesson/${meta.lessonId}`);
          setOpen(false);
        }
      }
    },
    [markAsRead, navigate],
  );

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('notifications.title')}
          className="relative h-9 w-9 rounded-full transition-all duration-200 hover:bg-muted"
        >
          <Bell
            className={cn(
              'h-[18px] w-[18px] transition-transform',
              bellAnimating && 'animate-bounce',
            )}
          />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground shadow-lg shadow-destructive/30">
              {unreadCount > 99 ? '99' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        collisionPadding={16}
        className="w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border/60 bg-card p-0 shadow-2xl sm:w-96"
        sideOffset={8}
      >
        {/* Header */}
        <div className="border-b border-border/40 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">
                {t('notifications.title')}
              </h3>
              {unreadCount > 0 && (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary-ink">
                  {t('notifications.newCount', { count: unreadCount })}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 text-xs font-medium text-primary-ink transition-colors hover:text-primary-ink/80"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('notifications.markAllRead')}
                </span>
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div className="mt-2.5 flex gap-1">
            {(['all', 'unread'] as FilterTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={cn(
                  'rounded-lg px-3 py-1 text-xs font-medium transition-all duration-200',
                  filter === tab
                    ? 'bg-primary/15 text-primary-ink'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {tab === 'all'
                  ? t('notifications.filters.all')
                  : t('notifications.filters.unread')}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable list */}
        <div
          ref={scrollRef}
          className="max-h-[70vh] overflow-y-auto overscroll-contain sm:max-h-[480px]"
          style={{ scrollbarWidth: 'thin' }}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary-ink" />
              <p className="mt-2 text-xs text-muted-foreground">
                {t('notifications.loading')}
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/50">
                {filter === 'unread' ? (
                  <CheckCheck className="h-7 w-7 text-primary-ink" />
                ) : (
                  <BellOff className="h-7 w-7 text-muted-foreground" />
                )}
              </div>
              <p className="mt-3 text-sm font-medium text-foreground">
                {filter === 'unread'
                  ? t('notifications.empty.unreadTitle')
                  : t('notifications.empty.allTitle')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {filter === 'unread'
                  ? t('notifications.empty.unreadDescription')
                  : t('notifications.empty.allDescription')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {groups.map((group) => (
                <NotificationItem
                  key={group.notification.id}
                  notification={group.notification}
                  grouped={
                    group.count > 1
                      ? {
                          count: group.count,
                          points: group.points,
                          unread: group.unreadIds.length > 0,
                        }
                      : undefined
                  }
                  onRead={() => handleRead(group.notification, group.unreadIds)}
                />
              ))}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
